import React, { useState } from 'react';
import moment from 'moment';
// import { arrayOf, bool, func, object, string } from 'prop-types';
import classNames from 'classnames';

import { useIntl, FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { formatMoney } from '../../../util/currency';
import { ensureListing } from '../../../util/data';
import { isPriceVariationsEnabled, requireListingImage } from '../../../util/configHelpers';

import { AspectRatioWrapper, ResponsiveImage, ListingCardThumbnail } from '../../../components';
import { boothTypes } from '../../../config/configListing';

import businessDefaultImage from '../../../assets/sitelogo.png';

import css from './SearchMapInfoCard.module.css';

const filterClasses = {
  signedUp: css.signedUp, // No shadow
  boothConfigured: css.boothConfigured, // Black shadow
  onboarded: css.onboarded, // Green shadow
  activeEngaged: css.activeEngaged, // Purple shadow
  tradeShow: css.tradeShow, // Blue shadow
};

// ListingCard is the listing info without overlayview or carousel controls
const ListingCard = props => {
  const {
    className,
    clickHandler,
    intl,
    isInCarousel,
    listing,
    urlToListing,
    config
  } = props;
  const { attributes, profileImage } = listing.author || {};
  const { tag, rfpBids = 0, acceptedBids = 0, ratings = 0, totalRatings = 0 } = attributes.profile.publicData || {};

  const { title, price, createdAt, publicData } = listing.attributes;
  const { cardStyle } = publicData || {};
  const formattedPrice =
    price && price.currency === config.currency
      ? formatMoney(intl, price)
      : price?.currency
        ? price.currency
        : null;
  const firstImage = profileImage
    ? profileImage
    : listing.images && listing.images.length > 0
      ? listing.images[0]
      : null;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const pricePerUnit = intl.formatMessage(
    { id: 'SearchMapInfoCard.perUnit' },
    { unitType: publicData?.unitType }
  );
  const priceValue = formattedPrice ? formattedPrice : '';

  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(
    conf => conf.listingType === publicData?.listingType
  );
  const showListingImage = requireListingImage(foundListingTypeConfig);
  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, foundListingTypeConfig);
  const hasMultiplePriceVariants = isPriceVariationsInUse && publicData?.priceVariants?.length > 1;

  const priceMessage = hasMultiplePriceVariants
    ? intl.formatMessage(
      { id: 'SearchMapInfoCard.priceStartingFrom' },
      { priceValue, pricePerUnit }
    )
    : intl.formatMessage({ id: 'SearchMapInfoCard.price' }, { priceValue, pricePerUnit });

  // listing card anchor needs sometimes inherited border radius.
  const classes = classNames(
    css.anchor,
    css.borderRadiusInheritTop,
    { [css.borderRadiusInheritBottom]: !isInCarousel },
    className
  );

  const {
    city,
    region,
    postcode,
    dbaName,
    address1,
    boothType,
    boothTypeChilds,
    companyName,
    stripeOnBoard,
  } = publicData || {};
  const getBoothFilter = boothTypes.filter((st) => boothType && boothType.length && boothType.includes(st.key));

  // Determine which class to apply based on conditions
  let appliedClass = '';

  if (tag == 'Trade Show') {
    appliedClass = filterClasses.tradeShow; // Filter 5
  } else if ((rfpBids > 0) || (ratings > 0)) {
    appliedClass = filterClasses.activeEngaged; // Filter 4
  } else if (stripeOnBoard) {
    appliedClass = filterClasses.onboarded; // Filter 3
  } else if (boothType && boothType.length) {
    appliedClass = filterClasses.boothConfigured; // Filter 2
  } else {
    appliedClass = filterClasses.signedUp; // Filter 1
  }

  return (
    // <a
    //   alt={title}
    //   className={classes}
    //   href={urlToListing}
    //   onClick={e => {
    //     e.preventDefault();
    //     // Use clickHandler from props to call internal router
    //     clickHandler(listing);
    //   }}
    // >
    <div
      className={classNames(css.card, css.borderRadiusInheritTop, appliedClass, {
        [css.borderRadiusInheritBottom]: !isInCarousel,
        [css.complete]: stripeOnBoard,
        [css.incomplete]: !stripeOnBoard,
      })}
    >
      {showListingImage ? (
        <AspectRatioWrapper
          className={css.aspectRatioWrapper}
          width={aspectWidth}
          height={aspectHeight}
        >
          {firstImage
            ? <ResponsiveImage
              rootClassName={classNames(css.rootForImage, css.borderRadiusInheritTop)}
              alt={title}
              noImageMessage={intl.formatMessage({ id: 'SearchMapInfoCard.noImage' })}
              image={firstImage}
              variants={variants}
              sizes="250px"
            />
            : <img
              className={css.businessDefaultImage}
              alt={"defaultLogo"}
              src={businessDefaultImage}
            />}
        </AspectRatioWrapper>
      ) : (
        <ListingCardThumbnail
          style={cardStyle}
          listingTitle={title}
          className={css.aspectRatioWrapper}
          width={aspectWidth}
          height={aspectHeight}
        />
      )}
      <div className={classNames(css.info, { [css.borderRadiusInheritBottom]: !isInCarousel })}>
        <div className={classNames(css.price, { [css.noPriceSetLabel]: !formattedPrice })}>
          {/* {priceMessage} */}
          {formattedPrice}
        </div>
        {/* <div className={css.name}>{title}</div> */}
        <div className={css.hoverData}>
          <h3 className={css.cardHeading} onClick={(e) => {
            e.preventDefault();
            const authorId = listing?.author?.id?.uuid;
            if (authorId) {
              return window.open(`${process.env.REACT_APP_MARKETPLACE_ROOT_URL}/u/${authorId}`, "_self")
            }
          }}>{companyName || title} ({dbaName})</h3>
          {city && region
            ? <p className={css.details}>
              <span><FormattedMessage id="ListingCard.city" /></span>
              {city + ', ' + region}
            </p>
            : null}
          {getBoothFilter && getBoothFilter.length
            ? <p className={css.booths}>
              <FormattedMessage id="ListingCard.booths" />
              {/* {getBoothFilter.map((st) => st.key == 'other' && boothTypeChilds && boothTypeChilds.boothTypeOther ? boothTypeChilds.boothTypeOther : st.label).join(", ")} */}
              {getBoothFilter.map((st) => st.key == 'other' && boothTypeChilds && boothTypeChilds.boothTypeOther ? boothTypeChilds.boothTypeOther: st.label).join(", ")}
            </p>
            : null}
          <p className={css.details}>
            <span><FormattedMessage id="ListingCard.rating" /></span>
            {ratings <= 1 ? ratings + ' Star' : '5 Stars'} ({totalRatings > 1 ? (totalRatings + ' Reviews') : (totalRatings + ' Review')})
          </p>
          <p className={css.details}>
            <span><FormattedMessage id="ListingCard.rfpsCreated" /></span>
            {rfpBids}
          </p>
          <p className={css.details}>
            <span><FormattedMessage id="ListingCard.rfpsBidOn" /></span>
            {acceptedBids}
          </p>
          <p className={css.details}>
            <span><FormattedMessage id="ListingCard.memberSince" /></span>
            {moment(createdAt).format('MMM DD, YYYY')}
          </p>
        </div>
      </div>
    </div>
    // </a>
  );
};

/**
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {Array<propTypes.listing>} props.listings - The listings
 * @param {Function} props.onListingInfoCardClicked - The function to handle the listing info card click
 * @param {Function} props.createURLToListing - The function to create the URL to the listing
 * @param {Object} props.config - The configuration
 * @returns {JSX.Element}
 */
const SearchMapInfoCard = props => {
  const [currentListingIndex, setCurrentListingIndex] = useState(0);
  const intl = useIntl();
  const {
    className,
    rootClassName,
    listings,
    createURLToListing,
    onListingInfoCardClicked,
    config,
  } = props;
  const currentListing = ensureListing(listings[currentListingIndex]);
  const hasCarousel = listings.length > 1;

  const classes = classNames(rootClassName || css.root, className);
  const caretClass = classNames(css.caret, { [css.caretWithCarousel]: hasCarousel });

  return (
    <div className={classes}>
      <div className={css.caretShadow} />
      <ListingCard
        clickHandler={onListingInfoCardClicked}
        urlToListing={createURLToListing(currentListing)}
        listing={currentListing}
        intl={intl}
        isInCarousel={hasCarousel}
        config={config}
      />
      {hasCarousel ? (
        <div className={classNames(css.paginationInfo, css.borderRadiusInheritBottom)}>
          <button
            className={css.paginationPrev}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              setCurrentListingIndex(
                prevListingIndex => (prevListingIndex + listings.length - 1) % listings.length
              );
            }}
          />
          <div className={css.paginationPage}>
            {`${currentListingIndex + 1}/${listings.length}`}
          </div>
          <button
            className={css.paginationNext}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              setCurrentListingIndex(
                prevListingIndex => (prevListingIndex + listings.length + 1) % listings.length
              );
            }}
          />
        </div>
      ) : null}
      <div className={caretClass} />
    </div>
  );
};

export default SearchMapInfoCard;
